import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { usePropertyListingsInfinite } from "@/hooks/usePropertyListings";
import { useAvailability } from "@/hooks/useAvailability";
import PropertyCard from "@/components/PropertyCard";
import PropertyCardSkeleton from "@/components/PropertyCardSkeleton";
import AvailabilitySearch, { SearchFilters } from "@/components/AvailabilitySearch";
import { Search, SlidersHorizontal, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

const PropertyGrid = () => {
  // Support deep-link search via ?q= (advertised in the sitewide SearchAction JSON-LD
  // and used by AI shopping agents to link straight to a filtered results view).
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const [search, setSearch] = useState(initialQuery);
  const [sortBy, setSortBy] = useState("default");
  const [guestFilter, setGuestFilter] = useState("any");
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    checkIn: undefined,
    checkOut: undefined,
    adults: 0,
    children: 0,
    bedrooms: 0,
    petFriendly: false,
  });

  // If arriving with ?q=, scroll straight to the results section so the query is visible.
  useEffect(() => {
    if (!initialQuery) return;
    const el = document.getElementById("properties");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [initialQuery]);


  const totalGuests = searchFilters.adults + searchFilters.children;
  const hasDateSearch = !!searchFilters.checkIn && !!searchFilters.checkOut;

  const {
    data: availableIds,
    isLoading: isSearching,
    isError: availabilityError,
    refetch: refetchAvailability,
  } = useAvailability({
    checkIn: searchFilters.checkIn,
    checkOut: searchFilters.checkOut,
    totalGuests,
  });

  // When dates are selected, constrain server-side pagination to the available subset.
  // Wait for the availability RPC to resolve before kicking off listing pages.
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePropertyListingsInfinite({
    availableIds: hasDateSearch ? (availableIds ?? null) : null,
    enabled: !hasDateSearch || !isSearching,
  });

  const properties = useMemo(
    () => data?.pages.flat() ?? [],
    [data]
  );

  // Date filter is now handled server-side via availableIds. Only text/sort/guest
  // filters still need the full dataset loaded for client-side filtering.
  const hasClientSideFilters =
    search.trim().length > 0 ||
    guestFilter !== "any" ||
    sortBy !== "default" ||
    totalGuests > 0 ||
    searchFilters.bedrooms > 0 ||
    searchFilters.petFriendly;

  // When client-side filters are active, ensure we have the full (possibly availability-constrained)
  // dataset so filtering works across all matching properties — not just the first page.
  useEffect(() => {
    if (hasClientSideFilters && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasClientSideFilters, hasNextPage, isFetchingNextPage, fetchNextPage, properties.length]);

  // Auto-load next page when sentinel scrolls into view (only when no active filters).
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (hasClientSideFilters) return;
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasClientSideFilters, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const filtered = useMemo(() => {
    const query = search.toLowerCase();
    let result = properties.filter((p) =>
      p.title.toLowerCase().includes(query) ||
      (p.internal_listing_name?.toLowerCase().includes(query) ?? false) ||
      p.amenities.some((a) => a.toLowerCase().includes(query))
    );

    if (hasDateSearch && availableIds) {
      result = result.filter((p) => availableIds.includes(p.id));
    }

    if (totalGuests > 0) {
      result = result.filter((p) => p.sleeps >= totalGuests);
    }

    if (searchFilters.bedrooms > 0) {
      result = result.filter((p) => p.bedrooms >= searchFilters.bedrooms);
    }

    if (searchFilters.petFriendly) {
      result = result.filter((p) => p.pets_allowed === true);
    }

    if (guestFilter !== "any") {
      const min = parseInt(guestFilter);
      result = result.filter((p) => p.sleeps >= min);
    }

    if (sortBy === "price-low") result = [...result].sort((a, b) => a.price_per_night - b.price_per_night);
    if (sortBy === "price-high") result = [...result].sort((a, b) => b.price_per_night - a.price_per_night);
    if (sortBy === "sleeps") result = [...result].sort((a, b) => b.sleeps - a.sleeps);

    return result;
  }, [properties, search, sortBy, guestFilter, availableIds, hasDateSearch, totalGuests, searchFilters.bedrooms, searchFilters.petFriendly]);

  // Carry the searched dates/guests onto each property link so guests don't re-enter them.
  const cardSearchQuery = useMemo(() => {
    if (!hasDateSearch || !searchFilters.checkIn || !searchFilters.checkOut) return undefined;
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const params = new URLSearchParams({
      check_in: iso(searchFilters.checkIn),
      check_out: iso(searchFilters.checkOut),
    });
    if (totalGuests > 0) {
      params.set("adults", String(searchFilters.adults || totalGuests));
      if (searchFilters.children > 0) params.set("children", String(searchFilters.children));
    }
    return params.toString();
  }, [hasDateSearch, searchFilters.checkIn, searchFilters.checkOut, searchFilters.adults, searchFilters.children, totalGuests]);

  const handleSearch = (filters: SearchFilters) => {
    setSearchFilters(filters);
    // Bring the results into view so the guest isn't left staring at the search box.
    if (filters.checkIn && filters.checkOut) {
      requestAnimationFrame(() => {
        document.getElementById("search-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const stillLoadingForFilters = hasClientSideFilters && hasNextPage;

  return (
    <section id="properties" className="py-20 md:py-28 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-accent font-semibold mb-3">
            Curated Collection
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground tracking-tight leading-[1.05] mb-4">
            Our Beach Houses
          </h2>
          <p className="text-muted-foreground font-body text-lg max-w-xl mx-auto">
            Hand-picked properties for the perfect Gulf Coast getaway
          </p>
        </div>

        {/* Availability Search */}
        <div className="mb-10 max-w-5xl mx-auto">
          <AvailabilitySearch onSearch={handleSearch} isSearching={isSearching} />
        </div>

        {/* Text Search & Filters */}
        <div className="grid grid-cols-2 md:flex md:flex-row gap-3 md:gap-4 mb-10 max-w-3xl mx-auto [&>*:first-child]:col-span-2 md:[&>*:first-child]:flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              aria-label="Search properties by name, internal title, or amenity"
              placeholder="Search by name or amenity..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 font-body bg-card border-border rounded-full"
            />
          </div>
          <Select value={guestFilter} onValueChange={setGuestFilter}>
            <SelectTrigger aria-label="Filter properties by guest capacity" className="w-full md:w-44 font-body rounded-full bg-card">
              <SlidersHorizontal className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Guests" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any size</SelectItem>
              <SelectItem value="6">6+ guests</SelectItem>
              <SelectItem value="10">10+ guests</SelectItem>
              <SelectItem value="14">14+ guests</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger aria-label="Sort properties" className="w-full md:w-44 font-body rounded-full bg-card">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Featured</SelectItem>
              <SelectItem value="price-low">Price: Low → High</SelectItem>
              <SelectItem value="price-high">Price: High → Low</SelectItem>
              <SelectItem value="sleeps">Most guests</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results info — when dates are active, availableIds is the authoritative total
            and `filtered.length` reflects what's been paginated in so far. */}
        <div id="search-results" className="scroll-mt-24" />
        {hasDateSearch && !isSearching && availableIds && (
          <div className="max-w-md mx-auto mb-8 space-y-2">
            <div className="flex items-center justify-between text-sm font-body text-muted-foreground">
              <span>
                Showing <span className="font-semibold text-foreground">{filtered.length}</span> of{" "}
                <span className="font-semibold text-foreground">{availableIds.length}</span>{" "}
                {availableIds.length === 1 ? "property" : "properties"} available for your dates
              </span>
              {isFetchingNextPage && (
                <Loader2 className="h-4 w-4 animate-spin text-accent shrink-0 ml-2" />
              )}
            </div>
            <Progress
              value={
                availableIds.length > 0
                  ? Math.min(100, (filtered.length / availableIds.length) * 100)
                  : 100
              }
              className="h-1.5"
            />
          </div>
        )}

        {/* Grid */}
        {hasDateSearch && availabilityError ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <p className="font-body text-muted-foreground">
              We couldn't check availability — your connection may have dropped.
            </p>
            <button
              type="button"
              onClick={() => refetchAvailability()}
              className="rounded-full bg-primary px-6 py-2 font-body font-semibold text-primary-foreground"
            >
              Try again
            </button>
          </div>
        ) : isLoading || isSearching ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        ) : filtered.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((property) => (
                <PropertyCard key={property.id} property={property} searchQuery={cardSearchQuery} />
              ))}
            </div>

            {/* Infinite scroll sentinel + Load more fallback */}
            {hasNextPage && !hasClientSideFilters && (
              <div ref={sentinelRef} className="flex justify-center mt-10">
                {isFetchingNextPage ? (
                  <Loader2 className="h-6 w-6 animate-spin text-accent" />
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    className="rounded-full font-body"
                  >
                    Load more properties
                  </Button>
                )}
              </div>
            )}

            {stillLoadingForFilters && (
              <div className="flex justify-center mt-10">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            )}
          </>
        ) : stillLoadingForFilters ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <PropertyCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground font-body text-lg">
              {hasDateSearch
                ? "No properties available for your selected dates and guests. Try different dates."
                : "No properties match your search. Try adjusting your filters."}
            </p>
          </div>
        )}

        <p className="mt-10 text-center text-xs font-body text-muted-foreground max-w-2xl mx-auto">
          Note: Pricing is dynamic and varies based on seasonality, holidays, and duration. Enter your exact dates to view live availability and pricing.
        </p>
      </div>
    </section>
  );
};

export default PropertyGrid;
